import { useCallback, useMemo, useRef, useState } from "react";
import AddIcon from "@mui/icons-material/Add";
import PaymentIcon from "@mui/icons-material/Payment";
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Paper,
  Popover,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { match } from "ts-pattern";
import type { Promisable } from "type-fest";

import { dayjs } from "@thalamos/common";
import type {
  ExtendedThalamosUser,
  FormContextData,
  Section12PaymentRequestOutcomeType,
  Section12PaymentRequestSummary,
} from "@thalamos/aspire-common";
import {
  canDoctorEditPaymentRequest,
  canDoctorInitiateS12Payments,
  DOCTOR_DELETABLE_PAYMENT_STATUSES,
  formIsMedRec,
  getBaseFormTemplate,
  getFullFormName,
  isDoctorAccreditation,
} from "@thalamos/aspire-common";

import {
  noMedRecTarget,
  workItemRecordsNoMedRec,
} from "../helpers/formContextLogic.js";
import { isGuestDoctorForFormContext } from "../helpers/workItemHelpers.js";
import type { S12PaymentRequestDialogHints } from "./S12PaymentRequestDialog.js";
import {
  assertPaymentRequestWriteAccepted,
  S12PaymentRequestDialog,
} from "./S12PaymentRequestDialog.js";
import {
  findPriorRequestLink,
  signedMedRecOptions,
} from "./signedMedRecOptions.js";
import { api } from "~/api.js";
import {
  MenuFlyout,
  renderErrorToast,
  renderSuccessToast,
  type MenuOptionsType,
} from "~/components/design-system/index.js";
import {
  STATUS_CHIP_SX,
  STATUS_COLORS,
  STATUS_LABELS,
} from "~/pages/Section12PaymentRequests/payment-request-helpers.js";
import { useEditPaymentRequest } from "~/pages/Section12PaymentRequests/useEditPaymentRequest.js";

interface DoctorPaymentRequestsSectionProps {
  formContext: FormContextData;
  user: ExtendedThalamosUser;
  reloadFormContext: () => void;
}

/**
 * Section shown to doctors on their admission form page that displays
 * their existing payment requests and allows them to create new ones.
 */
export function DoctorPaymentRequestsSection({
  formContext,
  user,
  reloadFormContext,
}: DoctorPaymentRequestsSectionProps) {
  const [showDialog, setShowDialog] = useState(false);
  const [dialogHints, setDialogHints] = useState<
    S12PaymentRequestDialogHints | undefined
  >(undefined);
  const {
    editingRequest,
    loadingEditId,
    startEdit: handleStartEdit,
    stopEdit: clearEditingRequest,
  } = useEditPaymentRequest();
  const [deletingRequest, setDeletingRequest] =
    useState<Section12PaymentRequestSummary | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const handleConfirmDelete = useCallback(async () => {
    if (!deletingRequest) return;
    setIsDeleting(true);
    try {
      assertPaymentRequestWriteAccepted(
        await api.section12.deletePaymentRequest(deletingRequest.id),
      );
      renderSuccessToast({ message: "Payment request deleted" });
      setDeletingRequest(null);
      reloadFormContext();
    } catch (error) {
      console.error("Failed to delete payment request:", error);
      renderErrorToast({ message: "Failed to delete payment request" });
    } finally {
      setIsDeleting(false);
    }
  }, [deletingRequest, reloadFormContext]);

  // Check if user is an S12 doctor
  const hasDoctorAccreditation = user.accreditations.some(
    isDoctorAccreditation,
  );
  const canSubmitAsGuest = isGuestDoctorForFormContext(user, formContext);

  // Get this doctor's payment requests for this form context
  const doctorRequests = useMemo(() => {
    const requests = formContext.section12PaymentRequests ?? [];
    return requests.filter(
      (pr: Section12PaymentRequestSummary) => pr.doctorUserId === user.id,
    );
  }, [formContext.section12PaymentRequests, user.id]);

  // Build a map of form ID to form name for display
  const formIdToNameMap = useMemo(() => {
    const map = new Map<string, string>();
    formContext.forms.forEach((form) => {
      if (formIsMedRec(form)) {
        const template = getBaseFormTemplate(form.formTemplate.id);
        const formName = template
          ? getFullFormName(template)
          : "Medical Recommendation";
        map.set(form.id, formName);
      }
    });
    return map;
  }, [formContext.forms]);

  const noMedRecWorkItem = useMemo(
    () => noMedRecTarget(formContext, user).workItem,
    [formContext, user],
  );

  const doctorSignedForms = useMemo(
    () => signedMedRecOptions({ forms: formContext.forms, userId: user.id }),
    [formContext.forms, user.id],
  );

  // Helper to get outcome type label
  const getOutcomeTypeLabel = (
    outcomeType: Section12PaymentRequestOutcomeType | undefined,
  ): string =>
    match(outcomeType)
      .with("form", () => "Medical Recommendation")
      .with("no-med-rec", () => "No medical recommendation")
      .with("completed-outside-thalamos", () => "Completed outside eMHA")
      .with("call-out-fee", () => "No assessment done (call out fee)")
      .with(undefined, () => "Legacy request")
      .exhaustive();

  const buildDialogHints = (): S12PaymentRequestDialogHints | undefined => {
    if (workItemRecordsNoMedRec(noMedRecWorkItem)) {
      return {
        initialOutcomeType: "no-med-rec",
      };
    }

    // If the doctor has signed forms, suggest the first one with no request on
    // it or on any form it replaced through a redo
    if (doctorSignedForms.length > 0) {
      const requestedFormIds = new Set(
        doctorRequests.flatMap((r) => (r.formId ? [r.formId] : [])),
      );
      const unclaimedForm = doctorSignedForms.find(
        (f) =>
          findPriorRequestLink({
            formId: f.formId,
            forms: formContext.forms,
            requestedFormIds,
          }) === undefined,
      );
      if (unclaimedForm) {
        return {
          initialOutcomeType: "form",
          initialFormId: unclaimedForm.formId,
        };
      }
    }

    return undefined;
  };

  const handleOpenDialog = () => {
    setDialogHints(buildDialogHints());
    setShowDialog(true);
  };

  const handleCloseDialog = () => {
    setShowDialog(false);
    setDialogHints(undefined);
  };

  // Don't render if user is not an S12 doctor
  if (!hasDoctorAccreditation && !canSubmitAsGuest) {
    return null;
  }

  // Don't render unless the doctor may initiate payment requests
  const paymentsEnabled =
    user.sessionOrganisationConfiguration != null &&
    canDoctorInitiateS12Payments(user.sessionOrganisationConfiguration);
  if (!paymentsEnabled) {
    return null;
  }

  return (
    <>
      <Paper sx={{ p: 2, mb: 2 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: doctorRequests.length > 0 ? 2 : 0,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <PaymentIcon color="action" />
            <Typography
              variant="subtitle1"
              sx={{
                fontWeight: "medium",
              }}
            >
              My Payment Requests
            </Typography>
            {doctorRequests.length > 0 && (
              <Chip
                label={doctorRequests.length}
                size="small"
                color="default"
              />
            )}
          </Box>
          <Button
            variant="outlined"
            size="small"
            startIcon={<AddIcon />}
            onClick={handleOpenDialog}
            data-testid="submit-payment-request-button"
          >
            Submit Request
          </Button>
        </Box>

        {doctorRequests.length === 0 ? (
          <Typography
            variant="body2"
            sx={{
              color: "text.secondary",
            }}
          >
            You have not submitted any payment requests for this assessment yet.
          </Typography>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Reference</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Form / Reason</TableCell>
                  <TableCell>Submitted</TableCell>
                  <TableCell>AMHP Team</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {doctorRequests.map((request) => {
                  const outcomeLabel = getOutcomeTypeLabel(request.outcomeType);
                  const formName = request.formId
                    ? formIdToNameMap.get(request.formId)
                    : null;

                  return (
                    <TableRow key={request.id}>
                      <TableCell>
                        <Typography
                          variant="body2"
                          sx={{ fontFamily: "monospace" }}
                        >
                          {request.paymentReference}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{outcomeLabel}</Typography>
                      </TableCell>
                      <TableCell>
                        {request.outcomeType === "form" && formName && (
                          <Typography variant="body2">{formName}</Typography>
                        )}

                        {request.outcomeType === "no-med-rec" &&
                          request.noMedRecReason && (
                            <Typography variant="body2">
                              {request.noMedRecReason}
                            </Typography>
                          )}

                        {!formName && !request.noMedRecReason && (
                          <Typography
                            variant="body2"
                            sx={{
                              color: "text.secondary",
                              fontStyle: "italic",
                            }}
                          >
                            —
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {dayjs(request.created).format("DD/MM/YY HH:mm")}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography
                          variant="body2"
                          sx={
                            request.amhpTeamName
                              ? undefined
                              : {
                                  color: "text.secondary",
                                  fontStyle: "italic",
                                }
                          }
                        >
                          {request.amhpTeamName ?? "Unknown Team"}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <StatusChip
                          status={request.status}
                          rejectedReason={request.rejectedReason}
                        />
                      </TableCell>
                      <TableCell>
                        <DoctorPaymentRequestRowActions
                          request={request}
                          isEditLoading={loadingEditId === request.id}
                          onEdit={() => {
                            void handleStartEdit(request);
                          }}
                          onDelete={() => {
                            setDeletingRequest(request);
                          }}
                        />
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>
      {showDialog && (
        <S12PaymentRequestDialog
          formContext={formContext}
          user={user}
          onClose={handleCloseDialog}
          reloadFormContext={reloadFormContext}
          hints={dialogHints}
        />
      )}
      {editingRequest && (
        <S12PaymentRequestDialog
          key={`${editingRequest.id}:${editingRequest.updated}`}
          formContext={formContext}
          user={user}
          editingRequest={editingRequest}
          onClose={clearEditingRequest}
          reloadFormContext={() => {
            clearEditingRequest();
            reloadFormContext();
          }}
        />
      )}
      <Dialog
        open={deletingRequest !== null}
        onClose={() => {
          if (!isDeleting) setDeletingRequest(null);
        }}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Delete payment request?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This will remove the request from your records. This cannot be
            undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setDeletingRequest(null);
            }}
            disabled={isDeleting}
          >
            Cancel
          </Button>
          <Button
            color="error"
            variant="contained"
            disabled={isDeleting}
            onClick={() => {
              void handleConfirmDelete();
            }}
          >
            {isDeleting ? "Deleting..." : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

interface DoctorPaymentRequestRowActionsProps {
  request: Section12PaymentRequestSummary;
  isEditLoading: boolean;
  onEdit: () => Promisable<void>;
  onDelete: () => Promisable<void>;
}

function StatusChip({
  status,
  rejectedReason,
}: {
  status: Section12PaymentRequestSummary["status"];
  rejectedReason?: string | null;
}) {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const hasReason = status === "rejected" && !!rejectedReason;

  return (
    <>
      <Chip
        label={STATUS_LABELS[status]}
        size="small"
        color={STATUS_COLORS[status]}
        onClick={
          hasReason
            ? (e) => {
                setAnchorEl(e.currentTarget);
              }
            : undefined
        }
        sx={{
          ...STATUS_CHIP_SX[status],
          ...(status === "pending" ? { color: "text.primary" } : {}),
          ...(hasReason ? { cursor: "pointer" } : {}),
        }}
      />
      {hasReason && (
        <Popover
          open={Boolean(anchorEl)}
          anchorEl={anchorEl}
          onClose={() => {
            setAnchorEl(null);
          }}
          anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
          transformOrigin={{ vertical: "top", horizontal: "left" }}
        >
          <Box sx={{ p: 1.5, maxWidth: 280 }}>
            <Typography
              variant="caption"
              color="text.secondary"
              display="block"
              gutterBottom
            >
              Rejection reason
            </Typography>
            <Typography variant="body2">{rejectedReason}</Typography>
          </Box>
        </Popover>
      )}
    </>
  );
}

function DoctorPaymentRequestRowActions({
  request,
  isEditLoading,
  onEdit,
  onDelete,
}: DoctorPaymentRequestRowActionsProps) {
  const cardRef = useRef<HTMLDivElement>(null);
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  // Guest-backed requests can't be fully edited/reassigned (the update path
  // requires an "amhp" team); limited edits still work. See
  // canDoctorEditPaymentRequest for the shared rule.
  const canEdit = canDoctorEditPaymentRequest(request);
  const canDelete = (
    DOCTOR_DELETABLE_PAYMENT_STATUSES as readonly string[]
  ).includes(request.status);

  if (!canEdit && !canDelete) {
    return null;
  }

  const options: MenuOptionsType[] = [
    ...(canEdit
      ? [
          {
            icon: "edit",
            name: "Edit",
            disabled: isEditLoading,
            onClick: onEdit,
          },
        ]
      : []),
    ...(canDelete
      ? [{ icon: "delete", name: "Delete", disabled: false, onClick: onDelete }]
      : []),
  ];

  return (
    <Box
      ref={cardRef}
      // Toggle from the render-time value (not a functional updater): picking a
      // menu item fires MenuFlyout's onClose (setIsMenuOpen(false)) and the
      // click then bubbles here in the same React event. The snapshot value
      // (true) resolves to false and closes; a functional updater would see the
      // queued false and flip it back to true, reopening the menu.
      onClick={(e) => {
        e.stopPropagation();
        setIsMenuOpen(!isMenuOpen);
      }}
    >
      <MenuFlyout
        cardRef={cardRef}
        options={options}
        isOpen={isMenuOpen}
        onClose={() => {
          setIsMenuOpen(false);
        }}
      />
    </Box>
  );
}
